// src/auth/useSubscription.ts
//
// Single source of truth for "does this user have paid access?" in client code.
// Wraps the profile query so every feature gate reads from the same shape.
//
// Status semantics (from the Stripe webhook):
//   - 'active'    : paid subscription, full access
//   - 'trialing'  : in free trial, full access (treat same as active)
//   - 'expired'   : was active, now not (canceled, past due past grace, etc)
//   - 'none'      : never subscribed
//
// isActive = 'active' | 'trialing' - the ONE flag feature gates should read.

import { useEffect } from 'react';
import { useCloudQuery } from '../data/hooks';
import { notify } from '../data/cloud/_shared';
import { fetchProfile } from '../data/cloud';
import { trackEvent } from '../utils/analytics';
import { getPlatform } from '../utils/native';

export type SubscriptionStatus = 'active' | 'trialing' | 'expired' | 'none';

export interface SubscriptionState {
  /** Raw status from the profile. 'none' if profile not loaded yet. */
  status: SubscriptionStatus;
  /** Has paid access right now (includes trial). Most gates read this. */
  isActive: boolean;
  /** Specifically in a free trial. */
  isTrialing: boolean;
  /** Had access before, lost it (cancel / unpaid / expired trial). */
  isExpired: boolean;
  /** Free tier - never subscribed. Use for "upgrade" prompts. */
  isFree: boolean;
  /** True while the initial profile fetch is in flight. */
  loading: boolean;
}

// Module-level so ONE watcher spans every hook instance: premium
// reference content (peptides/stacks) is stripped server-side by
// entitlement, so whenever isActive flips mid-session - purchase
// confirmed, webhook landing late, cross-device subscribe, trial
// expiring - the cached reference data is wrong and must refetch.
// null = initial load not observed yet (no notify on app start).
let lastKnownIsActive: boolean | null = null;

export function useSubscription(): SubscriptionState {
  const { data: profile, loading } = useCloudQuery('user_profiles', fetchProfile, null);
  const status: SubscriptionStatus = profile?.subscriptionStatus ?? 'none';
  const isActive = status === 'active' || status === 'trialing';

  useEffect(() => {
    if (loading) return;
    if (lastKnownIsActive === null) {
      lastKnownIsActive = isActive;
      return;
    }
    if (lastKnownIsActive !== isActive) {
      // false -> true means entitlement just turned on client-side: the
      // observed completion of a purchase/trial start (or a late webhook /
      // cross-device subscribe). The null-initial guard above means app
      // start with an already-active sub does NOT reach here, so this never
      // double-fires on boot. Module-level flag => fires once per activation.
      const becameActive = isActive;
      lastKnownIsActive = isActive;
      notify('peptides');
      notify('peptide_stacks');
      if (becameActive) {
        void trackEvent('purchase_completed', { status, platform: getPlatform() });
      }
    }
  }, [loading, isActive, status]);

  return {
    status,
    isActive,
    isTrialing: status === 'trialing',
    isExpired: status === 'expired',
    isFree: status === 'none',
    loading,
  };
}
